import React, { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { formatZAR, formatCredits, relativeTime } from '../../utils/format';

export type FloatHealth = 'green' | 'amber' | 'red';
export type FloatUnit = 'ZAR' | 'credits';

export interface SupplierFloatRow {
  supplierId: string;
  supplierName: string;
  currentBalance: number;
  minimumBalance: number;
  deficit: number;
  unit?: FloatUnit;
  health: FloatHealth;
  status: string;
  updatedAt: string;
}

function formatFloatAmount(value: number, unit: FloatUnit = 'ZAR', signed = false): string {
  const formatted = unit === 'credits' ? formatCredits(value) : formatZAR(value);
  if (!signed) return formatted;
  if (value > 0) return `+${formatted}`;
  return formatted;
}

type SortKey = 'supplierName' | 'currentBalance' | 'minimumBalance' | 'deficit' | 'updatedAt';

interface SupplierFloatTableProps {
  rows: SupplierFloatRow[];
  onRowClick: (supplierId: string) => void;
}

const HEALTH_CONFIG: Record<
  FloatHealth,
  {
    icon: typeof CheckCircle2;
    label: string;
    rowAccent: string;
    pillClass: string;
    iconClass: string;
  }
> = {
  green: {
    icon: CheckCircle2,
    label: 'Healthy',
    rowAccent: 'border-l-[var(--primary)]',
    pillClass:
      'border border-[#86BE41]/25 bg-[#86BE41]/10 text-[#5a8f2a]',
    iconClass: 'text-[var(--primary)]',
  },
  amber: {
    icon: AlertTriangle,
    label: 'Warning',
    rowAccent: 'border-l-[var(--mymoolah-blue)]',
    pillClass:
      'border border-[#2D8CCA]/25 bg-[#2D8CCA]/10 text-[#1a5f94]',
    iconClass: 'text-[var(--mymoolah-blue)]',
  },
  red: {
    icon: XCircle,
    label: 'Critical',
    rowAccent: 'border-l-[var(--destructive)]',
    pillClass:
      'border border-red-200 bg-red-50 text-red-700',
    iconClass: 'text-[var(--destructive)]',
  },
};

export const SupplierFloatTable: React.FC<SupplierFloatTableProps> = ({ rows, onRowClick }) => {
  const [sortKey, setSortKey] = useState<SortKey>('deficit');
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let av: string | number = a[sortKey];
      let bv: string | number = b[sortKey];
      if (sortKey === 'updatedAt') {
        av = new Date(a.updatedAt).getTime();
        bv = new Date(b.updatedAt).getTime();
      }
      if (typeof av === 'string') {
        return sortAsc ? av.localeCompare(String(bv)) : String(bv).localeCompare(av);
      }
      return sortAsc ? Number(av) - Number(bv) : Number(bv) - Number(av);
    });
    return copy;
  }, [rows, sortKey, sortAsc]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(key === 'supplierName');
    }
  };

  const sortIndicator = (key: SortKey) => (sortKey === key ? (sortAsc ? ' ↑' : ' ↓') : '');

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <CheckCircle2 className="h-10 w-10 text-[var(--muted-foreground)]/60" aria-hidden />
        <p className="text-sm font-medium text-[var(--muted-foreground)]">No active supplier floats</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="sticky top-0 z-10 border-b border-[var(--border)] bg-[var(--card)] text-left">
            <th className="whitespace-nowrap px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Status
            </th>
            <th className="whitespace-nowrap px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              <button type="button" onClick={() => handleSort('supplierName')} className="hover:text-[var(--foreground)]">
                Supplier{sortIndicator('supplierName')}
              </button>
            </th>
            <th className="whitespace-nowrap px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              <button type="button" onClick={() => handleSort('currentBalance')} className="hover:text-[var(--foreground)]">
                Float balance{sortIndicator('currentBalance')}
              </button>
            </th>
            <th className="whitespace-nowrap px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              <button type="button" onClick={() => handleSort('minimumBalance')} className="hover:text-[var(--foreground)]">
                Min balance{sortIndicator('minimumBalance')}
              </button>
            </th>
            <th className="whitespace-nowrap px-5 py-3.5 text-right text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              <button type="button" onClick={() => handleSort('deficit')} className="hover:text-[var(--foreground)]">
                Headroom{sortIndicator('deficit')}
              </button>
            </th>
            <th className="whitespace-nowrap px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              <button type="button" onClick={() => handleSort('updatedAt')} className="hover:text-[var(--foreground)]">
                Updated{sortIndicator('updatedAt')}
              </button>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--border)]">
          {sorted.map((row) => {
            const cfg = HEALTH_CONFIG[row.health];
            const HealthIcon = cfg.icon;
            return (
              <tr
                key={row.supplierId}
                onClick={() => onRowClick(row.supplierId)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(row.supplierId);
                  }
                }}
                tabIndex={0}
                role="button"
                className={`cursor-pointer border-l-[3px] bg-[var(--card)] transition-colors hover:bg-[var(--muted)]/40 focus:outline-none focus-visible:bg-[var(--muted)]/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--primary)]/40 ${cfg.rowAccent}`}
              >
                <td className="px-5 py-3.5 align-middle">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.pillClass}`}
                  >
                    <HealthIcon className={`h-3.5 w-3.5 shrink-0 ${cfg.iconClass}`} aria-hidden />
                    {cfg.label}
                  </span>
                </td>
                <td className="px-5 py-3.5 align-middle">
                  <p className="font-semibold text-[var(--foreground)]">{row.supplierName}</p>
                  <p className="text-xs text-[var(--muted-foreground)]">{row.supplierId}</p>
                </td>
                <td className="px-5 py-3.5 text-right font-mono tabular-nums text-[var(--foreground)]">
                  {formatFloatAmount(row.currentBalance, row.unit)}
                </td>
                <td className="px-5 py-3.5 text-right font-mono tabular-nums text-[var(--muted-foreground)]">
                  {formatFloatAmount(row.minimumBalance, row.unit)}
                </td>
                <td
                  className={`px-5 py-3.5 text-right font-mono text-sm font-semibold tabular-nums ${
                    row.deficit >= 0 ? 'text-[var(--success-color)]' : 'text-[var(--destructive)]'
                  }`}
                >
                  {formatFloatAmount(row.deficit, row.unit, true)}
                </td>
                <td className="whitespace-nowrap px-5 py-3.5 text-xs text-[var(--muted-foreground)]">
                  {relativeTime(row.updatedAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
